import {useCallback, useRef, useState} from 'react'
import {StarIcon, StarFillIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, ButtonGroup, ConfirmationDialog, CounterLabel, IconButton, Tooltip} from '@primer/react'
import {Count} from '@github-ui/count'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useStarMutation} from './hooks/use-star-mutation'
import {StarListMenu} from './components/StarListMenu'
import styles from './StarButton.module.css'

/** Props for the StarButton component */
export interface StarButtonProps {
  /**
   * The visual style of the button
   * - "button-group": renders the star toggle and dropdown as a connected button group (default)
   * - "icon-button": renders only the star toggle as a standalone icon button, without dropdown (for use in mobile)
   * @default "button-group"
   */
  buttonType?: 'button-group' | 'icon-button'

  /** Whether the repository is currently starred by the user */
  starred: boolean
  /** Total number of stars on the repository */
  starCount: number
  /** URL path to star the repository */
  starPath: string
  /** URL path to unstar the repository */
  unstarPath: string
  /** Whether the current user has permission to star */
  canStar: boolean
  /** Whether the current user is logged in */
  isLoggedIn: boolean
  /** URL to fetch user lists on demand */
  listsApiPath: string
  /** URL to update list membership (PUT) */
  listsUpdatePath: string
  /** Full repository name with owner, e.g. "octocat/hello-world" */
  repoNameWithOwner?: string
  /** Whether the viewer is an EMU blocked from contributing */
  emuContributionBlocked?: boolean
}

/**
 * Star/unstar button with counter for repository headers.
 *
 * Always renders as a split button:
 * - Left: star/unstar toggle (or login link) with count
 * - Right: dropdown caret opening the star lists menu
 */
export function StarButton({
  buttonType = 'button-group',
  starred: initialStarred,
  starCount: initialCount,
  starPath,
  unstarPath,
  canStar,
  isLoggedIn,
  listsApiPath,
  listsUpdatePath,
  repoNameWithOwner,
  emuContributionBlocked,
}: StarButtonProps) {
  const [listsOpen, setListsOpen] = useState(false)
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const closeLists = useCallback(() => setListsOpen(false), [])

  const {
    starred,
    starCount,
    toggleStar,
    pendingUnstarConfirmation,
    confirmUnstar,
    dismissUnstarConfirmation,
    isLoading: starIsLoading,
    syncExternalState,
  } = useStarMutation({
    starPath,
    unstarPath,
    initialStarred,
    initialCount,
    listsApiPath,
    onUnstarSuccess: closeLists,
  })

  const handleToggleStar = useCallback(() => {
    sendClickAnalyticsEvent({
      category: 'REPOSITORY_OVERVIEW',
      action: starred ? 'click:unstar' : 'click:star',
      label: 'location:header;ref_cta:star_button',
    })
    toggleStar()
  }, [sendClickAnalyticsEvent, starred, toggleStar])

  if (!isLoggedIn) {
    const loginReturnPath = `/${repoNameWithOwner ?? ''}`
    const loginHref = `/login?return_to=${encodeURIComponent(loginReturnPath)}`
    return (
      <Tooltip text="You must be signed in to star a repository" type="description" direction="s">
        <Button
          as="a"
          href={loginHref}
          size="small"
          leadingVisual={StarIcon}
          data-testid="star-button"
          onClick={() =>
            sendClickAnalyticsEvent({
              category: 'REPOSITORY_OVERVIEW',
              action: 'click:star_login',
              label: 'location:header;ref_cta:star_button',
            })
          }
        >
          Star
          <CounterLabel className="ml-1">
            <Count value={starCount} />
          </CounterLabel>
        </Button>
      </Tooltip>
    )
  }

  const starTooltip = starred
    ? `Unstar ${repoNameWithOwner ?? 'this repository'}`
    : `Star ${repoNameWithOwner ?? 'this repository'}`
  const disabledTooltip = emuContributionBlocked
    ? 'You cannot star repositories outside of your enterprise'
    : `You can't star ${repoNameWithOwner ?? 'this repository'}`

  const ariaText = canStar ? starTooltip : disabledTooltip

  const isButtonLoading = starIsLoading || Boolean(pendingUnstarConfirmation)

  const toggleButton =
    buttonType === 'button-group' ? (
      <Tooltip text={ariaText} type="description" direction="s">
        <Button
          size="small"
          loading={isButtonLoading}
          inactive={!canStar}
          leadingVisual={() => (starred ? <StarFillIcon className={styles.starredIcon} /> : <StarIcon />)}
          onClick={canStar ? handleToggleStar : undefined}
          data-testid="star-button"
          aria-label={ariaText}
        >
          <span>{starred ? 'Starred' : 'Star'}</span>
          <CounterLabel className="ml-1">
            <Count value={starCount} />
          </CounterLabel>
        </Button>
      </Tooltip>
    ) : (
      <IconButton
        size="small"
        loading={isButtonLoading}
        inactive={!canStar}
        icon={() => (starred ? <StarFillIcon className={styles.starredIcon} /> : <StarIcon />)}
        onClick={canStar ? handleToggleStar : undefined}
        data-testid="star-button"
        aria-label={ariaText}
      />
    )

  return (
    <>
      {(!canStar || buttonType === 'icon-button') && toggleButton}
      {canStar && buttonType === 'button-group' && (
        <>
          <ButtonGroup>
            {toggleButton}
            <IconButton
              ref={dropdownButtonRef}
              icon={TriangleDownIcon}
              inactive={!canStar}
              size="small"
              aria-label="Star lists"
              aria-haspopup="dialog"
              data-testid="star-dropdown-button"
              onClick={() => {
                sendClickAnalyticsEvent({
                  category: 'REPOSITORY_OVERVIEW',
                  action: 'click:star_lists_open',
                  label: 'location:header;ref_cta:star_lists_dropdown',
                })
                setListsOpen(prev => !prev)
              }}
            />
          </ButtonGroup>
          {listsOpen && (
            <StarListMenu
              anchorRef={dropdownButtonRef}
              onClose={closeLists}
              listsApiPath={listsApiPath}
              repoStarred={starred}
              starCount={starCount}
              listsUpdatePath={listsUpdatePath}
              onStarStateChange={syncExternalState}
            />
          )}
        </>
      )}

      {pendingUnstarConfirmation && (
        <ConfirmationDialog
          title="Unstar this repository?"
          confirmButtonType="danger"
          confirmButtonContent="Unstar"
          cancelButtonContent="Cancel"
          onClose={gesture => {
            if (gesture === 'confirm') {
              sendClickAnalyticsEvent({
                category: 'REPOSITORY_OVERVIEW',
                action: 'click:unstar_confirm',
                label: 'location:header;ref_cta:unstar_confirmation_dialog',
              })
              confirmUnstar()
            } else {
              sendClickAnalyticsEvent({
                category: 'REPOSITORY_OVERVIEW',
                action: 'click:unstar_cancel',
                label: 'location:header;ref_cta:unstar_confirmation_dialog',
              })
              dismissUnstarConfirmation()
            }
          }}
        >
          <p>
            This will remove {pendingUnstarConfirmation.repoNameWithOwner} from the{' '}
            {pendingUnstarConfirmation.listsWithCount} that it&apos;s been added to.
          </p>
        </ConfirmationDialog>
      )}
    </>
  )
}
